import React, { useRef, memo } from 'react';
import { motion, useInView } from 'framer-motion';
import { XCircle, AlertTriangle, TrendingDown, Lock } from 'lucide-react';
export function ProblemSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const fadeUp = (delay = 0) => ({
    initial: {
      opacity: 0,
      y: 40
    },
    animate: inView ? {
      opacity: 1,
      y: 0
    } : {},
    transition: {
      duration: 0.7,
      delay
    }
  });
  return <section ref={ref} className="py-24 px-4" style={{
    backgroundColor: '#050A14'
  }} data-id="element-156">
      <div className="max-w-4xl mx-auto" data-id="element-157">
        {/* Heading */}
        <motion.h2 {...fadeUp(0)} className="text-3xl md:text-4xl font-black text-center mb-8 leading-tight" style={{
        color: '#F1F5F9'
      }} data-id="element-158">
          Papá, mamá...{' '}
          <span style={{
          background: 'linear-gradient(90deg, #38BDF8, #818CF8)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }} data-id="element-159">
            el mundo en el que crecimos ya no existe.
          </span>
        </motion.h2>

        <motion.p {...fadeUp(0.1)} className="text-lg leading-relaxed text-center mb-10 max-w-3xl mx-auto" style={{
        color: '#94A3B8'
      }} data-id="element-160">
          Hiciste el sacrificio de venir a este país, de trabajar horas extra y
          de ahorrar cada dólar para que tus hijos tuvieran las oportunidades
          que tú no tuviste. Pero hay una verdad incómoda que nadie en el
          sistema escolar te está diciendo:
        </motion.p>

        {/* Callout */}
        <motion.div {...fadeUp(0.2)} className="mb-14 p-7 md:p-9 rounded-2xl text-center" style={{
        backgroundColor: '#0C1628',
        borderLeft: '4px solid #0EA5E9',
        border: '1px solid rgba(14,165,233,0.18)',
        borderLeft: '4px solid #0EA5E9'
      }} data-id="element-161">
          <p className="text-xl md:text-2xl font-black leading-snug" style={{
          color: '#F1F5F9'
        }} data-id="element-162">
            El sistema educativo actual está entrenando a tus hijos para empleos
            que la Inteligencia Artificial hará{' '}
            <span style={{
            color: '#0EA5E9'
          }} data-id="element-163">
              GRATIS en 5 años.
            </span>
          </p>
        </motion.div>

        {/* Three problem cards */}
        <div className="grid md:grid-cols-3 gap-5 mb-12" data-id="element-164">
          {[{
          icon: Lock,
          title: 'La Trampa de la Obediencia',
          body: 'Si tu hijo solo aprende a obedecer, a memorizar y a seguir reglas, lo están preparando para ser reemplazado por un algoritmo que no duerme.',
          color: '#F43F5E'
        }, {
          icon: TrendingDown,
          title: 'Títulos de Papel Mojado',
          body: 'El sistema educativo actual entrena a tus hijos para empleos que la IA hará gratis en 5 años. La universidad ya no es un refugio seguro.',
          color: '#818CF8'
        }, {
          icon: AlertTriangle,
          title: 'Consumidores Pasivos',
          body: 'La mayoría de los niños son "esclavos del algoritmo", consumiendo contenido sin fin en lugar de aprender a controlar la tecnología.',
          color: '#F59E0B'
        }].map((card, i) => {
          const Icon = card.icon;
          return <motion.div key={i} {...fadeUp(0.3 + i * 0.1)} className="p-7 rounded-2xl" style={{
            backgroundColor: '#0C1628',
            border: `1px solid ${card.color}20`,
            borderTop: `3px solid ${card.color}`
          }} data-id="element-165">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{
              backgroundColor: `${card.color}12`
            }} data-id="element-166">
                  <Icon size={22} style={{
                color: card.color
              }} data-id="element-167" />
                </div>
                <h3 className="text-lg font-black mb-3" style={{
              color: '#F1F5F9'
            }} data-id="element-168">
                  {card.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{
              color: '#94A3B8'
            }} data-id="element-169">
                  {card.body}
                </p>
              </motion.div>;
        })}
        </div>

        {/* Two errors */}
        <motion.h3 {...fadeUp(0.55)} className="text-2xl font-black text-center mb-7" style={{
        color: '#F1F5F9'
      }} data-id="element-170">
          La mayoría de los padres cometen uno de estos dos errores:
        </motion.h3>

        <div className="grid md:grid-cols-2 gap-5 mb-12" data-id="element-171">
          <motion.div {...fadeUp(0.6)} className="p-7 rounded-2xl" style={{
          backgroundColor: '#0C1628',
          border: '1px solid rgba(244,63,94,0.2)'
        }} data-id="element-172">
            <div className="flex items-center gap-3 mb-3" data-id="element-173">
              <XCircle size={26} style={{
              color: '#F43F5E'
            }} data-id="element-174" />
              <span className="font-black text-base" style={{
              color: '#F43F5E'
            }} data-id="element-175">
                Error #1: Prohibir la tecnología
              </span>
            </div>
            <p className="text-sm leading-relaxed" style={{
            color: '#94A3B8'
          }} data-id="element-176">
              Dejan a sus hijos en una{' '}
              <strong style={{
              color: '#F1F5F9'
            }} data-id="element-177">
                desventaja competitiva total
              </strong>
              . Mientras otros niños aprenden a liderar las máquinas, los tuyos
              ni siquiera saben cómo funcionan.
            </p>
          </motion.div>

          <motion.div {...fadeUp(0.65)} className="p-7 rounded-2xl" style={{
          backgroundColor: '#0C1628',
          border: '1px solid rgba(245,159,11,0.2)'
        }} data-id="element-178">
            <div className="flex items-center gap-3 mb-3" data-id="element-179">
              <AlertTriangle size={26} style={{
              color: '#F59E0B'
            }} data-id="element-180" />
              <span className="font-black text-base" style={{
              color: '#F59E0B'
            }} data-id="element-181">
                Error #2: Uso libre de pantallas
              </span>
            </div>
            <p className="text-sm leading-relaxed" style={{
            color: '#94A3B8'
          }} data-id="element-182">
              Convierten a sus hijos en{' '}
              <strong style={{
              color: '#F1F5F9'
            }} data-id="element-183">
                consumidores pasivos
              </strong>{' '}
              — esclavos del algoritmo. Entretenidos, pero sin criterio ni
              liderazgo.
            </p>
          </motion.div>
        </div>

        {/* Closing */}
        <motion.div {...fadeUp(0.7)} className="text-center p-7 rounded-2xl" style={{
        backgroundColor: '#0C1628',
        border: '1px solid rgba(14,165,233,0.15)'
      }} data-id="element-184">
          <p className="text-xl md:text-2xl font-black leading-snug" style={{
          color: '#F1F5F9'
        }} data-id="element-185">
            Si tu hijo solo está aprendiendo a obedecer, a memorizar y a seguir
            reglas...{' '}
            <span style={{
            color: '#F43F5E'
          }} data-id="element-186">
              lo están preparando para ser reemplazado.
            </span>
          </p>
        </motion.div>
      </div>
    </section>;
}